import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { IGridLayoutV2 } from '../models/grid-layout-v2.models';
import { IODataResponse } from '../models/odata.models';
import { etagFromResponse, formatEtagForQuery, parseEtag } from '../utils/etag.util';
import { ApiService } from './api.service';
import { HttpService } from './http.service';

/**
 * Client for GridLayoutsV2 (`/api/v2/gridlayouts`).
 * Versions use the shared ETag helpers (store unquoted, send quoted).
 *
 * Caching Strategy (same as GridLayoutService v1):
 * - SessionStorage for instant UI within the current browser tab/session
 * - Cleared on logout / tab close
 * - Updated on create / update / delete
 */
@Injectable({
    providedIn: 'root',
})
export class GridLayoutV2Service {
    private readonly httpService = inject(HttpService);
    private readonly apiService = inject(ApiService);

    private readonly CACHE_PREFIX = 'grid_layout_v2_cache_';

    getLayoutsByKey(key: string, options?: { bypassCache?: boolean }): Observable<IGridLayoutV2[]> {
        if (!options?.bypassCache) {
            const cached = this.getCachedLayouts(key);
            if (cached) {
                return of(cached);
            }
        }

        return this.fetchLayoutsFromBackend(key);
    }

    createLayout(key: string, tabKey: string, content: string): Observable<IGridLayoutV2> {
        const url = this.apiService.pathUrl.gridLayoutV2Tab(key, tabKey);

        return this.httpService.postTextWithResponse$<IGridLayoutV2>(url, content).pipe(
            map(response => this.requireBody(response, key, tabKey)),
            tap(layout => this.upsertCachedLayout(layout)),
            catchError((error: HttpErrorResponse) => {
                console.error(`Error creating grid layout v2 "${key}/${tabKey}":`, error);
                return throwError(() => error);
            })
        );
    }

    updateLayout(key: string, tabKey: string, content: string, version: string): Observable<IGridLayoutV2> {
        const url = this.apiService.pathUrl.gridLayoutV2Tab(key, tabKey);
        const versionParam = formatEtagForQuery(version);
        const params = versionParam ? this.apiService.params({ version: versionParam }) : undefined;

        return this.httpService.putTextWithResponse$<IGridLayoutV2>(url, content, params).pipe(
            map(response => this.requireBody(response, key, tabKey)),
            tap(layout => this.upsertCachedLayout(layout)),
            catchError((error: HttpErrorResponse) => {
                console.error(`Error updating grid layout v2 "${key}/${tabKey}":`, error);
                return throwError(() => error);
            })
        );
    }

    /**
     * Create or update. On 409, reload version (bypass cache) and retry update once
     * (same approach as GridStateStorageService for v1 layouts).
     */
    saveLayout(key: string, tabKey: string, content: string, version: string | null): Observable<IGridLayoutV2> {
        const request$ = version ? this.updateLayout(key, tabKey, content, version) : this.createLayout(key, tabKey, content);

        return request$.pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.status !== 409) {
                    return throwError(() => error);
                }

                return this.resolveVersion(key, tabKey).pipe(
                    switchMap(currentVersion => {
                        if (!currentVersion) {
                            return throwError(() => error);
                        }
                        return this.updateLayout(key, tabKey, content, currentVersion);
                    })
                );
            })
        );
    }

    deleteLayout(key: string, tabKey: string): Observable<void> {
        const url = this.apiService.pathUrl.gridLayoutV2Tab(key, tabKey);

        return this.httpService.delete$<void>(url).pipe(
            tap(() => this.removeCachedLayout(key, tabKey)),
            map(() => undefined),
            catchError((error: HttpErrorResponse) => {
                if (error.status === 404) {
                    this.removeCachedLayout(key, tabKey);
                    return of(undefined);
                }
                console.error(`Error deleting grid layout v2 "${key}/${tabKey}":`, error);
                return throwError(() => error);
            })
        );
    }

    clearCache(key: string): void {
        try {
            sessionStorage.removeItem(`${this.CACHE_PREFIX}${key}`);
        } catch (error) {
            console.error(`Error clearing grid layout v2 cache for key "${key}":`, error);
        }
    }

    /**
     * Clear all v2 layout caches from SessionStorage.
     * Called on logout to ensure fresh data on next login.
     */
    clearAllCaches(): void {
        try {
            const keysToRemove: string[] = [];

            for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                if (key?.startsWith(this.CACHE_PREFIX)) {
                    keysToRemove.push(key);
                }
            }

            keysToRemove.forEach(key => sessionStorage.removeItem(key));

            if (keysToRemove.length > 0) {
                console.log(`Cleared ${keysToRemove.length} grid layout v2 cache entries on logout`);
            }
        } catch (error) {
            console.error('Error clearing all grid layout v2 caches:', error);
        }
    }

    private fetchLayoutsFromBackend(key: string): Observable<IGridLayoutV2[]> {
        const escapedKey = key.replaceAll("'", "''");
        const query = `?$filter=key eq '${escapedKey}'`;
        const url = this.apiService.pathUrl.gridLayoutsV2(query);

        return this.httpService.get$<IODataResponse<IGridLayoutV2>>(url).pipe(
            map(response =>
                (response?.value ?? []).map(layout => ({
                    ...layout,
                    version: parseEtag(layout.version) ?? '',
                }))
            ),
            tap(layouts => this.setCachedLayouts(key, layouts)),
            catchError((error: HttpErrorResponse) => {
                console.error(`Error loading grid layouts v2 for key "${key}":`, error);
                return throwError(() => error);
            })
        );
    }

    private resolveVersion(key: string, tabKey: string): Observable<string | null> {
        return this.getLayoutsByKey(key, { bypassCache: true }).pipe(
            map(layouts => layouts.find(layout => layout.tabIdentifier === tabKey)?.version || null)
        );
    }

    private requireBody(response: HttpResponse<IGridLayoutV2>, key: string, tabKey: string): IGridLayoutV2 {
        const body = response.body;
        if (!body) {
            throw new Error(`Empty response saving grid layout v2 "${key}/${tabKey}"`);
        }

        return {
            ...body,
            version: parseEtag(body.version) ?? etagFromResponse(response) ?? '',
        };
    }

    private getCachedLayouts(key: string): IGridLayoutV2[] | null {
        try {
            const cached = sessionStorage.getItem(`${this.CACHE_PREFIX}${key}`);
            if (cached === null) {
                return null;
            }

            const parsed = JSON.parse(cached) as IGridLayoutV2[];
            return Array.isArray(parsed) ? parsed : null;
        } catch (error) {
            console.error(`Error reading cached grid layouts v2 for key "${key}":`, error);
            return null;
        }
    }

    private setCachedLayouts(key: string, layouts: IGridLayoutV2[]): void {
        try {
            sessionStorage.setItem(`${this.CACHE_PREFIX}${key}`, JSON.stringify(layouts));
        } catch (error) {
            console.error(`Error caching grid layouts v2 for key "${key}":`, error);
        }
    }

    private upsertCachedLayout(layout: IGridLayoutV2): void {
        const cached = this.getCachedLayouts(layout.key) ?? [];
        const index = cached.findIndex(item => item.tabIdentifier === layout.tabIdentifier);

        if (index >= 0) {
            cached[index] = layout;
        } else {
            cached.push(layout);
        }

        this.setCachedLayouts(layout.key, cached);
    }

    private removeCachedLayout(key: string, tabKey: string): void {
        const cached = this.getCachedLayouts(key);
        if (!cached) {
            return;
        }

        this.setCachedLayouts(
            key,
            cached.filter(layout => layout.tabIdentifier !== tabKey)
        );
    }
}
